import { useSelector } from "react-redux";
import {
  calculateEarning,
  ClientInfo,
  useEarnRoiCalculationContext,
} from "../Utility/Utility";
import { getSellingPrice, isLinePurchasable } from "./cartPricing";

const isEnabledConfigValue = (value) =>
  value === true ||
  value === 1 ||
  ["1", "true", "active"].includes(String(value ?? "").trim().toLowerCase());

const findConfigValue = (list, key) =>
  (Array.isArray(list) ? list : []).find(
    (item) => String(item?.configKey ?? "").trim().toLowerCase() === key
  )?.configValue;

/* Earn points for the cart, using the same rules and calculation as the
   product page (RedemptionDetails): calculateEarning with the client's earn
   ROI context, each product's minEarnPoints / maxEarnPoints and its lowest
   denomination as the base.
   - Per card: one card's price after discount (as the product page shows per denomination).
   - Order total: per product, selling price x qty + convenience fee + GST, summed
     over purchasable lines only. */
const useCartEarn = () => {
  const earnRoiCalculationContext = useEarnRoiCalculationContext();
  const clientData = ClientInfo()?.clientData;
  const clientConfigurationData = useSelector(
    (state) => state.clientConfigurationReducer?.clientConfigurationData?.[0]
  );

  const clientConfigurations = clientData?.clientConfigurations;
  const isEarnView = findConfigValue(clientConfigurations, "isearnview");
  const isEarn = findConfigValue(clientConfigurations, "isearn");
  const isEarnLabelShowOnly = isEnabledConfigValue(
    findConfigValue(clientConfigurationData?.clientConfig, "isearnlabelshowonly") ??
      clientConfigurationData?.isEarnLabelShowOnly ??
      findConfigValue(clientConfigurations, "isearnlabelshowonly")
  );
  const journeyType = clientData?.journeyType;
  const isWalletJourney =
    journeyType === true ||
    journeyType === 1 ||
    String(journeyType).trim().toLowerCase() === "true" ||
    String(journeyType).trim() === "1";
  const isEarnEnabled = isEarn !== "2" && isEarnView !== "2";
  // Per-card and "You'll earn" labels show only where the product page shows them.
  const showEarnLabels = isEarnLabelShowOnly && !isWalletJourney && isEarnEnabled;

  const earn = (details, amount) =>
    calculateEarning({
      ...earnRoiCalculationContext,
      amount,
      minEarnPoints: details?.minEarnPoints,
      minBurnPoints: details?.maxEarnPoints,
      baseDenomination: details?.rewardBaseDenomination,
    });

  const hasEarn = (details) => Number(details?.minEarnPoints) > 0;

  // Earn for one card of this line; null when it should not be shown.
  const getLineEarning = (line) => {
    if (!showEarnLabels || !hasEarn(line.details)) return null;
    const earning = earn(line.details, getSellingPrice(line));
    return earning.points > 0 ? earning : null;
  };

  // Earn for the order: purchasable lines grouped by product, then summed.
  const getTotalEarning = (lines) => {
    if (!isEarnEnabled) return null;
    const amountBySku = new Map();
    (lines || []).filter(isLinePurchasable).forEach((line) => {
      if (!hasEarn(line.details)) return;
      const qty = Number(line.quantity) || 0;
      const sellingTotal = getSellingPrice(line) * qty;
      const fee = (sellingTotal * (Number(line.details.convenienceFeePercent) || 0)) / 100;
      const gst = (fee * (Number(line.details.gstPercent) || 0)) / 100;
      const entry = amountBySku.get(line.sku) || { details: line.details, amount: 0 };
      entry.amount += sellingTotal + fee + gst;
      amountBySku.set(line.sku, entry);
    });

    const total = [...amountBySku.values()].reduce(
      (sum, { details, amount }) => {
        const earning = earn(details, amount);
        return { points: sum.points + earning.points, worth: sum.worth + earning.worth };
      },
      { points: 0, worth: 0 }
    );
    return total.points > 0 ? total : null;
  };

  return {
    showEarnLabels,
    // Wallet journey shows only the order total, as on the product page.
    showWalletTotal: isWalletJourney,
    isEarnLabelShowOnly,
    getLineEarning,
    getTotalEarning,
  };
};

export default useCartEarn;
